import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Check } from "lucide-react";
import { api, BRAND_LOGO_URL, BRAND_ICON_URL, formatApiError } from "@/lib/api";

export default function AdminBrandAssets() {
    const [status, setStatus] = useState({ logo_uploaded: false, icon_uploaded: false });
    const [refreshKey, setRefreshKey] = useState(0);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/brand");
            setStatus(data);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Brand status probe failed:", err?.message || err);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function upload(kind, file) {
        if (!file) return;
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        try {
            await api.post("/admin/brand/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${kind === "logo" ? "Official logo" : "Official app icon"} updated`);
            await load();
            setRefreshKey((k) => k + 1);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="admin-brand-assets">
            <div className="gsf-eyebrow">Brand</div>
            <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Brand Assets</h1>
            <p className="mt-3 text-sm text-gray-500 max-w-2xl">
                The official Global Seed Foundation logo and app icon are displayed across the public website, the
                admin dashboard, browser tabs, mobile devices and social sharing previews. Only PNG, JPG, WEBP, SVG or
                ICO files are accepted (max 8 MB).
            </p>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <UploadCard kind="logo" title="Official Logo" description="Full identity — used across the site." uploaded={status.logo_uploaded}
                    previewUrl={`${BRAND_LOGO_URL}?t=${refreshKey}`} onSelect={(f) => upload("logo", f)} testid="upload-logo" />
                <UploadCard kind="icon" title="Official App Icon" description="Square icon — favicon, PWA and sticky header." uploaded={status.icon_uploaded}
                    previewUrl={`${BRAND_ICON_URL}?t=${refreshKey}`} onSelect={(f) => upload("icon", f)} testid="upload-icon" />
            </div>
        </div>
    );
}

function UploadCard({ kind, title, description, uploaded, previewUrl, onSelect, testid }) {
    const inputRef = useRef(null);
    return (
        <div className="gsf-card p-8" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">{title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{description}</p>
                </div>
                {uploaded && (
                    <span className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
                        <Check size={14} /> Official
                    </span>
                )}
            </div>

            <div className="mt-6 flex items-center gap-6">
                <div className="w-28 h-28 rounded-2xl bg-[color:var(--gsf-soft-green)] p-2 flex items-center justify-center border border-[#e2e8de]">
                    {uploaded ? (
                        <img src={previewUrl} alt={title} className="max-w-full max-h-full object-contain" data-testid={`${testid}-preview`} />
                    ) : (
                        <div className="text-xs text-gray-500 text-center px-2">Not uploaded</div>
                    )}
                </div>
                <div className="flex-1">
                    <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.ico"
                        className="hidden" onChange={(e) => onSelect(e.target.files?.[0])} data-testid={`${testid}-file`} />
                    <button onClick={() => inputRef.current?.click()} className="gsf-btn-primary text-sm" data-testid={`${testid}-btn`}>
                        <UploadCloud size={16} /> {uploaded ? "Replace" : "Upload"}
                    </button>
                    <p className="mt-3 text-xs text-gray-500">
                        The asset is stored server-side and served via a signed cache-controlled endpoint. Uploading a new asset immediately replaces the previous one across the website.
                    </p>
                </div>
            </div>
        </div>
    );
}
